/**
 * The Peer Configuration dialog's roster — its master list (REQ-UI-139 / -140 /
 * -145; storyboard Flow W §5.1).
 *
 * Peers, not interfaces. Before Phase J this list held every interface in the
 * deployment flat, which had no way to say *which system* a flow served — and
 * "stop everything going to the fusion cell" was therefore an unknown number of
 * acts an operator had to already know how to enumerate.
 *
 * # Two counts, not a fraction
 *
 * Each row shows the peer's interface count **and** how many are started,
 * because one can be zero while the peer's own pill reads `started`: a started
 * peer with every interface stopped is emitting nothing, and a bare "3
 * interfaces" would conceal exactly that. Neither count is privileged — an
 * interface's existence and state are in the all-roles field-set (feature-spec
 * §2.10), while its endpoints and filter literals are not.
 *
 * # Absent, not disabled
 *
 * `Add peer` renders only for `admin` / `support_engineer` (REQ-UI-143). A
 * disabled Add button tells an operator the system has a capability they may not
 * use, which on the surface that governs what leaves the deployment is an
 * invitation to go looking for a way to use it.
 *
 * Cross-references:
 *   - REQ-UI-139 (the dialog opens on this), -140 (row contents), -143
 *     (absent-not-disabled), -145 (the empty state).
 *   - ADR-039 §D-1; feature-spec §3.4 / §5.1.
 */
import { StatePill } from "@/peers/peer-state-controls";
import type { PeerView } from "@/peers/peer-model";
import { cn } from "@/lib/utils";

/** Props for {@link PeerRoster}. */
export interface PeerRosterProps {
  peers: readonly PeerView[];
  selectedId: string | null;
  onSelect: (peerId: string) => void;
  /** Renders the create affordance. Absent — never disabled — for other roles. */
  mayMutate: boolean;
  onCreate: () => void;
  /**
   * Open the import dialog. Roster-level because importing CREATES a peer, so it
   * has no peer to belong to (REQ-UI-150). Absent — never disabled — for a
   * session that may not mutate, like `Add peer` beside it.
   */
  onImport: () => void;
  /** True while a create is in flight and its unsaved row should show. */
  creating: boolean;
  isLoading: boolean;
  error: string | null;
}

/** Render the peer roster. */
export function PeerRoster({
  peers,
  selectedId,
  onSelect,
  mayMutate,
  onCreate,
  onImport,
  creating,
  isLoading,
  error,
}: PeerRosterProps): React.ReactElement {
  return (
    <nav
      aria-label="Peers"
      data-testid="peer-roster"
      className="flex max-h-[35%] w-full flex-none flex-col border-b border-border bg-bg-base md:max-h-none md:w-[280px] md:border-b-0 md:border-r"
    >
      <div className="flex items-center gap-2 border-b border-border px-3 py-1.5">
        <span className="text-meta uppercase tracking-wide text-text-secondary">Peers</span>
        {mayMutate && (
          <div className="ml-auto flex items-center gap-1">
            <button
              type="button"
              onClick={onImport}
              data-testid="peer-import"
              title="Install a peer from a bundle on this workstation"
              className="rounded border border-border px-2 py-0.5 text-meta text-text-primary hover:bg-bg-elevated"
            >
              Import peer&hellip;
            </button>
            <button
              type="button"
              onClick={onCreate}
              data-testid="peer-add"
              className="rounded border border-border px-2 py-0.5 text-meta text-text-primary hover:bg-bg-elevated"
            >
              Add peer
            </button>
          </div>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        {isLoading ? (
          <p className="px-3 py-2 text-body-sm text-text-secondary">Loading…</p>
        ) : error !== null ? (
          // Distinct from the empty state on purpose: an empty list and a list
          // that failed to load have opposite implications on an egress surface.
          <p role="alert" className="px-3 py-2 text-body-sm text-destructive">
            Could not load peers: {error}
          </p>
        ) : peers.length === 0 && !creating ? (
          <p className="px-3 py-2 text-body-sm text-text-muted" data-testid="peer-roster-empty">
            No peers are configured. Nothing is leaving this deployment.
          </p>
        ) : (
          <ul>
            {peers.map((peer) => (
              <li key={peer.peer_id}>
                <PeerRow
                  peer={peer}
                  selected={selectedId === peer.peer_id}
                  onSelect={() => onSelect(peer.peer_id)}
                />
              </li>
            ))}
            {creating && (
              <li
                data-testid="peer-row-unsaved"
                className="border-l-2 border-brand bg-bg-elevated px-3 py-2 text-body-sm text-text-primary"
              >
                New peer (unsaved)
              </li>
            )}
          </ul>
        )}
      </div>
    </nav>
  );
}

/** One roster row: name, configured state, and the two interface counts. */
function PeerRow({
  peer,
  selected,
  onSelect,
}: {
  peer: PeerView;
  selected: boolean;
  onSelect: () => void;
}): React.ReactElement {
  const configured = peer.interface_count;
  const started = peer.started_interface_count;
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected ? "true" : undefined}
      data-testid={`peer-roster-row-${peer.name}`}
      className={cn(
        "flex w-full flex-col gap-1 border-l-2 px-3 py-2 text-left hover:bg-bg-elevated",
        selected ? "border-brand bg-bg-elevated" : "border-transparent",
      )}
    >
      <span className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-body-sm text-text-primary">{peer.name}</span>
        <StatePill state={peer.state} />
      </span>
      <span className="text-meta text-text-secondary" data-testid={`peer-counts-${peer.name}`}>
        {configured === undefined || started === undefined
          ? // Never "0 interfaces" when the counts did not arrive — on this
            // surface a guessed zero reads as "nothing is leaving through this
            // peer", which is a claim this row is in no position to make.
            "Interface count unavailable"
          : `${configured} ${configured === 1 ? "interface" : "interfaces"} · ${started} started`}
      </span>
    </button>
  );
}
